<template>
  <div class="searchable-select" :class="{ 'searchable-select--open': isOpen }" ref="containerRef">
    <!-- Trigger: shows selected choice or placeholder -->
    <div
      ref="triggerRef"
      class="searchable-select__trigger"
      :class="{
        'searchable-select__trigger--disabled': disabled,
        'searchable-select__trigger--autofilled': autofilled,
      }"
      :aria-label="triggerAriaLabel"
      tabindex="0"
      @click="toggleDropdown"
      @keydown.enter.prevent="onTriggerEnter"
      @keydown.space.prevent="onTriggerEnter"
      @keydown.arrow-down.prevent="onTriggerArrowDown"
      @keydown.arrow-up.prevent="onTriggerArrowUp"
      @keydown.escape="closeDropdown(true)"
    >
      <span v-if="selectedChoice" class="searchable-select__selected-name">
        {{ selectedChoice.content }}
        <template v-if="showPricing && selectedChoice.price">
          — {{ formatPrice(selectedChoice.price) }}
        </template>
      </span>
      <span v-else class="searchable-select__placeholder-text">{{ placeholder }}</span>
      <BaseIcon name="chevron" size="16" class="searchable-select__chevron" />
    </div>

    <!-- Dropdown -->
    <div
      v-if="isOpen"
      class="searchable-select__dropdown"
      :class="{ 'searchable-select__dropdown--up': openUp }"
    >
      <!-- Search input with magnify icon inside (only when the list scrolls) -->
      <div v-if="showSearch" class="searchable-select__search">
        <BaseIcon name="magnify" size="14" class="searchable-select__search-icon" />
        <input
          ref="searchInputRef"
          type="text"
          v-model="searchTerm"
          :placeholder="searchPlaceholder"
          :aria-label="searchPlaceholder"
          class="searchable-select__search-input"
          @input="onSearch"
          @keydown.escape="closeDropdown(true)"
          @keydown.enter.prevent="selectHighlighted"
          @keydown.arrow-down.prevent="navigateDown"
          @keydown.arrow-up.prevent="navigateUp"
          @keydown.tab="closeDropdown(false)"
        />
      </div>

      <div v-if="!filteredChoices.length" class="searchable-select__empty">
        {{ noResultsText }}
      </div>
      <div v-else class="searchable-select__options">
        <div
          v-for="(choice, index) in filteredChoices"
          :key="choice.id"
          class="searchable-select__option"
          :class="{
            'searchable-select__option--highlighted': index === highlightedIndex,
            'searchable-select__option--selected': choice.id === modelValue,
            'searchable-select__option--disabled': choice.disabled,
          }"
          @click="selectChoice(choice)"
          @mouseenter="highlightedIndex = index"
        >
          <span class="searchable-select__option-label">{{ choice.content }}</span>
          <span v-if="showPricing && choice.price" class="searchable-select__option-price">
            + {{ formatPrice(choice.price) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
  import BaseIcon from '../../BaseIcon.vue';
  import { shouldShowSearch, filterChoices } from '../../../utils/searchableChoice.js';

  export default {
    name: 'SearchableChoiceSelect',

    components: {
      BaseIcon,
    },

    props: {
      choices: {
        type: Array,
        required: true,
      },
      modelValue: {
        type: [String, Number],
        default: null,
      },
      showPricing: {
        type: Boolean,
        default: true,
      },
      // The field label, used as the combobox/listbox accessible name.
      label: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      // Show the search field only once the list is long enough to scroll.
      // Kept in sync with the options list `max-height: 260px` in the CSS
      // (≈ 6 rows visible before the list starts scrolling).
      maxVisibleOptions: {
        type: Number,
        default: 6,
      },
      // Marks the field as auto-filled (value seeded by the identical-options
      // auto-fill feature); renders a subtle brand-tinted highlight to match
      // the auto-fill cue on the other configurator layouts.
      autofilled: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      // Global $t (vue-i18n), matching how sibling elements translate strings
      // (e.g. SingleChoiceElement). Falls back to the literal when unavailable.
      const instance = getCurrentInstance();
      const t = (key, fallback) =>
        instance?.proxy?.$t ? instance.proxy.$t(key, fallback) : fallback;

      const isOpen = ref(false);
      const openUp = ref(false);
      const searchTerm = ref('');
      const highlightedIndex = ref(-1);
      const searchInputRef = ref(null);
      const containerRef = ref(null);
      const triggerRef = ref(null);

      const placeholder = computed(() =>
        t('form.fields.choice.select_placeholder', 'Selecteer een optie...')
      );
      const searchPlaceholder = computed(() =>
        t('form.fields.choice.search_placeholder', 'Zoek een optie...')
      );
      const triggerAriaLabel = computed(() => props.label || placeholder.value);
      const noResultsText = computed(() =>
        t('form.fields.choice.no_results', 'Geen resultaten gevonden')
      );

      // Approx. max dropdown height: search bar (~44px) + options list max-height (260px) + borders.
      const DROPDOWN_MAX_HEIGHT = 320;

      const selectedChoice = computed(
        () => (props.choices || []).find((c) => c.id === props.modelValue) || null
      );

      // The search field only earns its place when the list would scroll;
      // for short lists we show just the plain dropdown (consistent look).
      const showSearch = computed(() => shouldShowSearch(props.choices, props.maxVisibleOptions));

      // If the search field disappears while the dropdown is open (choice count
      // drops below the threshold), move focus back to the trigger so keyboard
      // navigation isn't lost to <body>.
      watch(showSearch, (hasSearch) => {
        if (isOpen.value && !hasSearch) triggerRef.value?.focus();
      });

      // Pure local filter — no API, matches on content and description.
      const filteredChoices = computed(() => filterChoices(props.choices, searchTerm.value));

      const openDropdown = async () => {
        if (props.disabled) return;
        // Flip upward when there isn't room below (e.g. the option is near the page bottom),
        // mirroring the auto-flip a native <select> gets from the OS for free.
        const rect = containerRef.value?.getBoundingClientRect();
        if (rect) {
          const spaceBelow = window.innerHeight - rect.bottom;
          openUp.value = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > spaceBelow;
        }
        isOpen.value = true;
        searchTerm.value = '';
        // Pre-highlight the current selection so reopening lands on the chosen option —
        // the web equivalent of a native <select> jumping to the selected item.
        highlightedIndex.value = filteredChoices.value.findIndex((c) => c.id === props.modelValue);
        await nextTick();
        if (showSearch.value) {
          searchInputRef.value?.focus();
        } else {
          // No search field — keep focus on the trigger so ↑/↓ still navigate.
          triggerRef.value?.focus();
        }
        scrollHighlightedIntoView();
      };

      // returnFocus=true moves focus back to the trigger — used on keyboard-driven
      // close (Escape, selection) so focus isn't lost to <body>. Outside-click
      // closes without stealing focus back.
      const closeDropdown = (returnFocus = false) => {
        isOpen.value = false;
        highlightedIndex.value = -1;
        if (returnFocus) {
          nextTick(() => triggerRef.value?.focus());
        }
      };

      // Scroll the highlighted (or selected) option into view within the options list.
      const scrollHighlightedIntoView = () => {
        const el =
          containerRef.value?.querySelector('.searchable-select__option--highlighted') ||
          containerRef.value?.querySelector('.searchable-select__option--selected');
        el?.scrollIntoView({ block: 'nearest' });
      };

      const toggleDropdown = () => {
        if (isOpen.value) {
          closeDropdown();
        } else {
          openDropdown();
        }
      };

      // Keyboard navigation from the trigger — only used when there is no search
      // field (short list); with a search field the input is focused and handles
      // its own arrow/enter keys, so these never fire.
      const onTriggerArrowDown = () => {
        if (!isOpen.value) {
          openDropdown();
        } else if (!showSearch.value) {
          navigateDown();
        }
      };

      const onTriggerArrowUp = () => {
        if (isOpen.value && !showSearch.value) navigateUp();
      };

      // Enter/Space on the trigger. Closed → open. Open with a highlighted
      // option (no search field) → select it. Open with nothing highlighted →
      // keep the list open rather than closing on a stray keypress.
      const onTriggerEnter = () => {
        if (!isOpen.value) {
          openDropdown();
          return;
        }
        if (!showSearch.value && highlightedIndex.value >= 0) {
          selectHighlighted();
        }
      };

      const onSearch = () => {
        highlightedIndex.value = -1;
      };

      const selectChoice = (choice) => {
        if (choice.disabled) return;
        emit('update:modelValue', choice.id);
        closeDropdown(true);
      };

      const selectHighlighted = () => {
        const choice = filteredChoices.value[highlightedIndex.value];
        if (choice) selectChoice(choice);
      };

      // Find the next selectable option in a direction, skipping disabled ones
      // (e.g. matrix-disabled colors) so the highlight never lands on an option
      // that Enter would silently ignore.
      const findEnabledIndex = (start, direction) => {
        const list = filteredChoices.value;
        for (let i = start + direction; i >= 0 && i < list.length; i += direction) {
          if (!list[i].disabled) return i;
        }
        return -1;
      };

      const navigateDown = () => {
        const next = findEnabledIndex(highlightedIndex.value, 1);
        if (next !== -1) {
          highlightedIndex.value = next;
          nextTick(scrollHighlightedIntoView);
        }
      };

      const navigateUp = () => {
        const prev = findEnabledIndex(highlightedIndex.value, -1);
        if (prev !== -1) {
          highlightedIndex.value = prev;
          nextTick(scrollHighlightedIntoView);
        }
      };

      const onOutsideClick = (event) => {
        if (containerRef.value && !containerRef.value.contains(event.target)) {
          if (isOpen.value) closeDropdown();
        }
      };

      onMounted(() => {
        document.addEventListener('mousedown', onOutsideClick);
      });

      onBeforeUnmount(() => {
        document.removeEventListener('mousedown', onOutsideClick);
      });

      // €0 never reaches here — both call sites render the price only
      // `v-if="... && price"`, so free choices show nothing (by design).
      const formatPrice = (price) =>
        new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
        }).format(price);

      return {
        isOpen,
        openUp,
        searchTerm,
        highlightedIndex,
        searchInputRef,
        containerRef,
        triggerRef,
        placeholder,
        searchPlaceholder,
        triggerAriaLabel,
        noResultsText,
        selectedChoice,
        showSearch,
        filteredChoices,
        openDropdown,
        toggleDropdown,
        closeDropdown,
        onTriggerArrowDown,
        onTriggerArrowUp,
        onTriggerEnter,
        onSearch,
        selectChoice,
        selectHighlighted,
        navigateDown,
        navigateUp,
        formatPrice,
      };
    },
  };
</script>

<style scoped lang="scss">
  // Styled with the configurator's --cs-* theme tokens (mirrors .sf-select in
  // ConfiguratorChoiceElement), reusing the .searchable-select structure from
  // the CRM SearchableLeadSelect.
  .searchable-select {
    position: relative;

    &__trigger {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.625rem 0.75rem;
      font-size: 1rem;
      line-height: 1.5;
      color: var(--cs-text-body, #374151);
      background-color: var(--cs-input-default-background, #fff);
      border: 1px solid var(--cs-input-default-border, #d1d5db);
      border-radius: var(--cs-border-radius, 0.5rem);
      cursor: pointer;
      user-select: none;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

      &:hover {
        border-color: var(--cs-input-hover-border, #9ca3af);
      }

      &--disabled {
        background-color: var(--cs-input-disabled-background, #f3f4f6);
        color: var(--cs-text-muted, #94a3b8);
        cursor: not-allowed;
        pointer-events: none;
      }

      // Auto-fill highlight, mirroring .sf-select--autofilled on the other
      // configurator layouts. Plain fallbacks first so browsers without
      // color-mix() (older Safari/webviews) still show a visible tint.
      &--autofilled {
        border-color: var(--cs-brand-primary, #3b82f6);
        border-color: color-mix(
          in srgb,
          var(--cs-brand-primary, #3b82f6) 40%,
          var(--cs-input-default-border, #d1d5db)
        );
        background-color: var(--cs-input-default-background, #fff);
        background-color: color-mix(
          in srgb,
          var(--cs-brand-primary, #3b82f6) 6%,
          var(--cs-input-default-background, #fff)
        );
      }
    }

    &--open &__trigger {
      border-color: var(--cs-brand-primary, #3b82f6);
      box-shadow: 0 0 0 3px var(--cs-brand-primary-light, rgba(59, 130, 246, 0.15));
    }

    &__selected-name {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__placeholder-text {
      flex: 1;
      color: var(--cs-text-muted, #6b7280);
    }

    &__chevron {
      flex-shrink: 0;
      color: var(--cs-text-muted, #6b7280);
      // chevron.svg points up by default; point it down when closed to match native selects
      transform: rotate(180deg);
      transition: transform 0.2s ease;
    }

    &--open &__chevron {
      transform: rotate(0deg);
    }

    &__dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background: var(--cs-input-default-background, #fff);
      border: 1px solid var(--cs-input-default-border, #d1d5db);
      border-radius: var(--cs-border-radius, 0.5rem);
      box-shadow:
        0 8px 24px -4px rgba(0, 0, 0, 0.12),
        0 2px 8px -2px rgba(0, 0, 0, 0.08);
      z-index: 1000;
      overflow: hidden;

      // Flip above the trigger when there isn't enough room below.
      &--up {
        top: auto;
        bottom: calc(100% + 4px);
      }
    }

    &__search {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--cs-input-default-border, #e5e7eb);
    }

    &__search-icon {
      flex-shrink: 0;
      color: var(--cs-text-muted, #9ca3af);
    }

    &__search-input {
      flex: 1;
      border: none;
      background: transparent;
      outline: none;
      font-size: 0.9375rem;
      color: var(--cs-text-body, #374151);

      &::placeholder {
        color: var(--cs-text-muted, #9ca3af);
      }
    }

    &__empty {
      padding: 1rem 0.75rem;
      text-align: center;
      color: var(--cs-text-muted, #6b7280);
      font-size: 0.875rem;
    }

    &__options {
      max-height: 260px;
      overflow-y: auto;
    }

    &__option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      cursor: pointer;
      border-bottom: 1px solid var(--cs-input-default-border, #f3f4f6);
      transition: background-color 0.15s ease;

      &:last-child {
        border-bottom: none;
      }

      &:hover,
      &--highlighted {
        background-color: var(--cs-choice-hover-background, #f9fafb);
      }

      &--selected {
        background-color: var(--cs-choice-active-background, #eff6ff);
        font-weight: 600;
      }

      &--disabled {
        color: var(--cs-text-muted, #9ca3af);
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    &__option-label {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__option-price {
      flex-shrink: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--cs-brand-primary, #3b82f6);
    }
  }
</style>
